import { ReactComponent as IconPrevious } from '@/assets/svgs/previous-arrow.svg'
import { assetUrl } from '@/utils/asset-url'
import AdditionalZone from '@/components/AdditionalZone'
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from '@/components/Button'
import Input from '@/components/Input'
import Portal from '@/components/Portal'
import SelectorBox2 from '@/components/SelectorBox2'
import useComputed from '@/hooks/use-computed'
import useConfig from '@/hooks/use-config'
import usePage from '@/hooks/use-page'
import "@/translations/i18n"
import rem from '@/utils/rem'
import classname from 'classnames'
import produce from 'immer'
import { isEmpty, orderBy } from 'lodash'
import { useEffect, useRef, useState, Fragment, useContext } from 'react'
import { useTranslation } from "react-i18next"
import KeyboardEventHandler from '@infinium/react-keyboard-event-handler'
import { useNavigate, useLocation } from '@/lib/router-dom'
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg'
import useModal from '@/hooks/use-modal/index';
import api from '@/services/api/simulateData.js'
import Toast2 from '@/components/Toast2'
import ReactDOM from 'react-dom';
import DatePicker from "react-datepicker"
import useBehalfPanel from '@/hooks/use-behalf-panel'
import { ToastContext } from '@/context/toast/index'
import { ReactComponent as IconConfigure } from '@/assets/svgs/icon-configure.svg';

import Ajv from "ajv";
import addFormats from "@/utils/custom-ajv-formats";
import Stepper from '@/componentsDS/Stepper/Stepper'

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const SimulateProvide = () => {

	/*=================================================
		General
	/================================================*/

	const { t } = useTranslation();
	const pageTitle = t("provideData");

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: [t("cdiPitstop"), t("shareData"), pageTitle, t("chooseDataElement")] })
	const { goTo } = useNavigate();
	const addToastMessage = useContext(ToastContext)

	/*=================================================
		Modal
	/================================================*/
	const { visible: isVisible, show: showSimulateProvideCancelModal, hide: hideSimulateProvideCancelModal } = useModal()
	const { visible: isSubmitModalVisible, show: showSimulateProvideSubmitModal, hide: hideSimulateProvideSubmitModal } = useModal()

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()

	/*=================================================
		API
	/================================================*/

	const { provideItems, error: errorConfig } = useConfig()
	const [selectedDataElement, setSelectedDataElement, [participants, querySchema]] = useComputed({},
		value => value && value.to ? orderBy(value.to, [participant => participant.name.toLowerCase()], ['asc']) : [],
		value => value.querySchema
	)
	const [selectedParticipants, setSelectedParticipants] = useState([])
	const [selectedOnBehalf, setSelectedOnBehalf] = useState(null)
	const [onBehalfOf, setOnBehalfOf] = useState([])

	useEffect(() => {
		setSelectedParticipants([])
	}, [selectedDataElement])

	useEffect(() => {
		setOnBehalfOf(selectedParticipants.reduce((pre, cur) => [...pre, ...cur.on_behalf_of], []))
	}, [selectedParticipants])

	const [behalfPanel] = useBehalfPanel(onBehalfOf)

	const ElementsList = () => {

		if (!provideItems) {
			return ''
		}

		const sortedProvideItems = orderBy(provideItems.filter(item => !isEmpty(item.querySchema)), [item => item.name.toLowerCase()], ['asc'])

		const list = sortedProvideItems.map((element, index) => {

			const active = selectedDataElement.id === element.id

			return (
				<div className="data-element-selectors__col" key={index} onClick={() => setSelectedDataElement(element)}>
					<SelectorBox2 label={element.name} active={active} />
				</div>
			)
		})

		return list.length > 0 ? list : <div className="data-element-selectors__col">There are no data elements to simulate provide</div>
	}

	const ParticipantsList = () => {

		if (!participants) {
			return ''
		}

		const list = participants.filter(participant => participant.id === "538a889b-df92-4e40-b796-bca7510501a5").map((element, index) => {

			const active = selectedParticipants.find(participant => participant.id === element.id)
			const activeIndex = selectedParticipants.findIndex(participant => participant.id === element.id)

			function toggleParticipant() {

				if (active) {
					const updatedSelectedParticipants = produce(selectedParticipants, draft => {
						draft.splice(activeIndex, 1)
					})
					return setSelectedParticipants(updatedSelectedParticipants)
				}

				const updatedSelectedParticipants = produce(selectedParticipants, draft => {
					draft.splice(activeIndex, 1)
					draft.push(element)
				})
				return setSelectedParticipants(updatedSelectedParticipants)
			}

			return (
				<div className="data-element-selectors__col" key={index} onClick={() => toggleParticipant()}>
					<SelectorBox2 label={element.name} image={element.iconUrl} active={active} />
				</div>
			)
		})

		return list.length > 0 ? list : <div className="data-element-selectors__col">There are no participants</div>

	}

	useEffect(() => {
		setError(errorConfig)
	}, [errorConfig])

	/*=================================================
		Steps
	/================================================*/
	const [steps, setSteps] = useState([
		{stepTitle: t("chooseDataElement")},
		{stepTitle: t("chooseParticipant")},
		{stepTitle: t("dataInformation")},
])
	const [step, setStep] = useState(1)

	const [stepOneStatus, setStepOneStatus] = useState(false)
	const [stepTwoStatus, setStepTwoStatus] = useState(false)
	const [stepThreeBStatus, setStepThreeBStatus] = useState(true)

	useEffect(() => {

		if (selectedDataElement && selectedDataElement.id) {
			return setStepOneStatus(true)
		}

		setStepOneStatus(false)
	}, [selectedDataElement])

	useEffect(() => {

		if (selectedParticipants.length > 0) {
			return setStepTwoStatus(true)
		}

		setStepTwoStatus(false)
	}, [selectedParticipants])

	useEffect(() => {

		const updatedBreadcrumbs = produce(breadcrumbsItems, draft => {
			draft[draft.length - 1] = steps[step - 1].stepTitle
		})
		return setBreadcrumbsItems(updatedBreadcrumbs)

	}, [step])

	const dataElementSelectorStyles = {
		// marginTop: rem(20)
	}

	function goToStep(step) {
		if (step < 1 || step > steps.length) {
			return
		}
		setStep(step)
	}

	function prevStep() {
		goToStep(step - 1)
	}

	function nextStep() {
		goToStep(step + 1)
	}

	function goBack() {
		goTo('/shared-data')
	}

	/*=================================================
		Queries
	/================================================*/

	const [errors, setErrors] = useState([])
	const queryForm = useRef(null)
	const [queries, setQueries] = useState()

	const parseQuery = (key, value, required = []) => {
		if (value.required) required = value.required
		if (value.type === 'object') {
			return Object.entries(value.properties).reduce((r, e) => [...r, ...parseQuery(e[0], e[1], required)], [])
		} else {
			return [
				{
					label: key.split('_').reduce((r, item) => `${r} ${item.slice(0, 1).toUpperCase()}${item.slice(1)}`, ''),
					type: value.type === 'number' ? 'number' : value.type === 'array' ? 'array' : !value.format ? 'string' : value.format,
					field: `${key}|${Math.random(1000)}`,
					description: value.description,
					required: required.includes(key)
				}
			]
		}
	}

	const getQuery = () => {
		let query
		if (querySchema.oneOf) {
			query = querySchema.oneOf.map(e => parseQuery(null, e))
		} else {
			query = [parseQuery(null, querySchema)]
		}
		setQueries(query)
	}

	useEffect(() => {
		if (!querySchema) {
			return
		}
		getQuery()
	}, [selectedDataElement])

	function onSelectedOnBehalf(params) {
		if (behalfPanel && params && params.id && params.id !== 'no') {
			setSelectedOnBehalf(params)
			setStepThreeBStatus(true)
		} else if (!behalfPanel) {
			setStepThreeBStatus(true)
		} else {
			setStepThreeBStatus(false)
		}
	}

	function onKeyDown(event) {
		if (event.key === 'Enter') {
			event.preventDefault()
			submit()
		}
	}

	const [submitLoading, setSubmitLoading] = useState()

	function convertStringtoIsoString(text) {
		const array = text.split(" ");
		const mergeDate = array[0] + "T" + array[1] + "." + array[2] + "Z"
		const convertDate = new Date(mergeDate)
		return convertDate.toISOString();
	}

	function checkRequiredFields() {
		const check = queryForm.current
		let queryParameter
		queries.find(query => {
				queryParameter = {}
				query.forEach(e => {
					const formValue = check[e.field]?.value;
					const value =
						e.type === 'string' ? (formValue === '' ? undefined : formValue)
						: e.type === 'number' ? (formValue === '' ? undefined : +formValue)
						: e.type === 'array' ? (formValue === '' ? undefined : formValue.split(','))
						: e.type === 'date-time' ? (formValue === '' ? undefined : convertStringtoIsoString(formValue))
						: (formValue === '' ? undefined : formValue)
					queryParameter[e.field.split('|')[0]] = value
				})
				return true
		})
		if (queryParameter?.from) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {}
			queryParameter.snapshot_dt.from = queryParameter.from
			delete queryParameter.from
		}
		if (queryParameter?.to) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {}
			queryParameter.snapshot_dt.to = queryParameter.to
			delete queryParameter.to
		}
		const validate = ajv.compile(querySchema);
		const valid = validate({ ...queryParameter });
		if (!valid) {
			setErrors(validate.errors);
		} else {
			setErrors();
			return showSimulateProvideSubmitModal()
		}
	}

	async function submit() {
		if (submitLoading) {
			return
		}
		setSubmitLoading(true)
		const fm = queryForm.current
		let queryParameter
		queries.find(query => {
			const valid = query.every(e => !(e.required && fm[e.field].value === ''))
			if (valid) {
				queryParameter = {}
				query.forEach(e => {
					const value =
						e.type === 'string' ? (fm[e.field].value === '' ? undefined : fm[e.field].value)
						: e.type === 'number' ? (fm[e.field].value === '' ? undefined : +fm[e.field].value)
						: e.type === 'array' ? (fm[e.field].value === '' ? undefined : fm[e.field].value.split(','))
						: e.type === 'date-time' ? (fm[e.field].value === '' ? undefined : convertStringtoIsoString(fm[e.field].value))
						: (fm[e.field].value === '' ? undefined : fm[e.field].value)

					queryParameter[e.field.split('|')[0]] = value
				})
				return true
			} else {
				return false
			}
		})
		if (queryParameter.from) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {}
			queryParameter.snapshot_dt.from = queryParameter.from
			delete queryParameter.from
		}
		if (queryParameter.to) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {}
			queryParameter.snapshot_dt.to = queryParameter.to
			delete queryParameter.to
		}
		if (!queryParameter) {
			return addToastMessage.addToastMessage([t("fillInAllFields")], 'error')
		}
		const payload = {
			participants: [{
				id: selectedParticipants[0].id
			}],
			parameters: queryParameter,
			on_behalf_of: selectedOnBehalf ? [selectedOnBehalf] : [],
		}
		const [error, data] = await api.simulate(selectedDataElement.id, payload, 'provide')
		setSubmitLoading(false)
		if (error) {
			return setError(error?.message || "Unable to simulate provide.")
		}
		goTo('/shared-data/share/success')
	}

	/*=================================================
		Handle Time Input for Date Component
	/================================================*/

	const [startDate, setStartDate] = useState();
	const [endDate, setEndDate] = useState();
	useEffect(() => {
		return setEndDate(null);
	}, [startDate]);

	/*=================================================
		Custom Date Picker
	/================================================*/

	const CustomDatePicker = ({ field, dateFormat, fileType, label }) => {

		// const [startDate, setStartDate] = useState(new Date());
		const CustomTimeInput = ({ date, value, onChange }) => (
			<input
				value={value}
				onChange={(e) => onChange(e.target.value)}
			/>
		);

		return (

			<>
				{
					fileType === "date-time" && label === ' From' && (
						<DatePicker
							selected={startDate}
							onChange={(date) => { setStartDate(date); handleChange(field, date) }}
							id={field}
							dateFormat={dateFormat}
							showTimeInput
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							customTimeInput={<CustomTimeInput />}
							placeHolderText="Select date and time"
							calendarClassName="hide__time"
						/>
					)
				}
				{
					fileType === "date-time" && label === ' To' && (
						<DatePicker
							selected={endDate}
							onChange={(date) => { setEndDate(date); handleChange(field, date) }}
							id={field}
							dateFormat={dateFormat}
							showTimeInput
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							customTimeInput={<CustomTimeInput />}
							calendarClassName="hide__time"
							placeholderText="Select date and time"
							minDate={startDate ? new Date(startDate) : new Date()}
						/>
					)
				}
				{
					fileType === "date-time" && label !== ' To' && label !== ' From' && (
						<DatePicker
							selected={endDate}
							onChange={(date) => { setEndDate(date); handleChange(field, date) }}
							id={field}
							dateFormat={dateFormat}
							showTimeSelect
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							customTimeInput={<CustomTimeInput />}
							calendarClassName="hide__time"
							placeholderText="Select date and time"
							isClearable={endDate ? true : false}
						/>
					)
				}
				{
					fileType !== "date-time" && label === ' From' && (
						<DatePicker
							selected={startDate}
							onChange={(date) => { setStartDate(date); handleChange(field, date) }}
							id={field}
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							dateFormat={dateFormat}
							placeholderText="Select date"
							isClearable
						/>
					)
				}
				{
					fileType !== "date-time" && label === ' To' && (
						<DatePicker
							selected={endDate}
							onChange={(date) => { setEndDate(date); handleChange(field, date) }}
							id={field}
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							dateFormat={dateFormat}
							placeholderText="Select date"
							isClearable
							minDate={startDate ? new Date(startDate) : new Date()}
						/>
					)
				}
				{
					fileType !== "date-time" && label !== ' To' && label !== ' From' && (
						<DatePicker
							selected={startDate}
							onChange={(date) => { setStartDate(date); handleChange(field, date) }}
							id={field}
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							dateFormat={dateFormat}
							placeholderText="Select date"
							isClearable={startDate ? true : false}
						/>
					)
				}
			</>
		)
	}

	function handleChange(field, event) {
		if (errors && errors.length > 0) {
			if (event) {
				let error = [...errors]
				const errorIndex = errors?.findIndex(
					(e) => e?.params?.missingProperty?.includes(field.split('|')[0]) || e?.params?.additionalProperty?.includes(field.split('|')[0]) || e.message?.includes(field.split('|')[0])
				);
				if (errorIndex > -1) {
					error.splice(errorIndex, 1);
					setErrors(error);
				}
			}
			else {
				checkRequiredFields()
			}
		}
	}

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>

				<div className="title-header__right">

				</div>
			</div>

			<Stepper  steps={steps} currentStep={step-1}/>

			<hr />

			{step === 1 && (
				<>
					<span className="t--share-data-subheader">{t("chooseDataElementToRetrieve")}</span>
					<div className="data-element-selectors" style={dataElementSelectorStyles}>
						<div className="data-element-selectors__cols">
							<ElementsList />
						</div>
					</div>
				</>
			)}

			{step === 2 && (
				<>
					<span className="t--share-data-subheader">{t("chooseWhomToRetrieved")}</span>
					<div className="data-element-selectors" style={dataElementSelectorStyles}>
						<div className="data-element-selectors__cols">
							<ParticipantsList />
						</div>
					</div>
				</>
			)}

			{step === 3 && (
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
					<div className="request" style={{ width: rem(556) }}>
						<div className="request__query">
							<div className="request__query__items">
								<form ref={queryForm}>
									{querySchema && (
										queries.map((qrys, index) => {
											return (<Fragment key={index}>
												{index > 0 && (<div className="request__or">or</div>)}
												{qrys.map((query, index) => {
													const { label, type, field, required } = query
													const label_required = `${label}${required ? ' *' : ''}`
													const error = errors?.find(
														(e) => e?.params?.missingProperty?.includes(field.split('|')[0]) || e?.params?.additionalProperty?.includes(field.split('|')[0]) || e.message?.includes(field)
													) ? `This is a mandatory field.` : "";
													return (
														<div className="request__query__item" key={index}>
															{type === 'string' && (
																<Input
																	label={label_required}
																	id={field}
																	onKeyDown={event => { onKeyDown(event) }}
																	tabIndex="0"
																	name={field}
																	change={(e) => handleChange(field, e.target.value)}
																	error={required && error}
																	errorMessage={error}
																/>
															)}
															{type === 'array' && (
																<Input
																	label={label_required}
																	id={field}
																	onKeyDown={event => { onKeyDown(event) }}
																	tabIndex="0"
																	name={field}
																	change={(e) => handleChange(field, e.target.value)}
																	error={required && error}
																	errorMessage={error}
																/>
															)}
															{type === 'date' && (
																<>
																	<div className='input-field__label'>{label_required}</div>
																	<CustomDatePicker
																		field={field}
																		dateFormat="yyyy-MM-dd"
																		fileType={type}
																		label={label}
																	/>
																	{error && required && <span className='input-field_required'>{error}</span>}
																</>
															)}
															{type === 'date-time' && (
																<>
																	<div className='input-field__label'>{label_required}</div>
																	<CustomDatePicker
																		field={field}
																		dateFormat="yyyy-MM-dd HH:mm:ss SSS"
																		fileType={type}
																		label={label}
																	/>
																	{error && required && <span className='input-field_required'>{error}</span>}
																</>
															)}
															{type === 'number' && (
																<Input
																	label={label_required}
																	id={field}
																	type="number"
																	name={field}
																	change={(e) => handleChange(field, e.target.value)}
																	error={required && error}
																	errorMessage={error}
																/>
															)}
															{type === 'int64' && (
																<Input
																	label={label_required}
																	id={field}
																	name={field}
																	change={(e) => handleChange(field, e.target.value)}
																	error={required && error}
																	errorMessage={error}
																/>
															)}
														</div>
													)
												})}
												{/* <hr /> */}
											</Fragment>)
										})
									)}
								</form>
							</div>
						</div>
						<div className="request__onbehalf">
							<div className="additional-zone__side">
								<AdditionalZone
									on_behalf_of={onBehalfOf}
									callback={params => onSelectedOnBehalf(params)}
								/>
							</div>
						</div>
					</div>
				</div>
			)}

			<div style={{ height: rem(72) }}></div>
			<div className="DS__layout__footer">
				<div className="layout-default__sticky-actions">
					<KeyboardEventHandler handleKeys={["alt+right", "alt+left", "enter", "esc"]} onKeyEvent={(key, e) => {
						if (key === "alt+left" || key === "esc") {
							prevStep()
						}
						if (key === "alt+right" || key === "enter") {
							if (stepOneStatus && step === 1) {
								nextStep()
							}
							if (stepTwoStatus && step === 2) {
								nextStep()
							}
							if (stepThreeBStatus && step === steps.length) {
								submit()
							}
						}
					}} />
					<div className="layout-default__sticky-actions__left">
						{step > 1 &&
							<Button text={t("Previous")}
								sizing='Medium' ghost full maxWidth='154'
								submit={() => prevStep()}
							/>
						}
					</div>
					<div className="layout-default__sticky-actions__right">
						<div className="overview__button_wrap">
							<Button text={t("Cancel")}
								sizing='Cancel' full noborder maxWidth='154'
								submit={() => showSimulateProvideCancelModal()}
							/>
							{step === 1 &&
								<Button text={t("next")}
									sizing='Medium' full maxWidth='154'
									disabled={!stepOneStatus}
									submit={() => nextStep()}
								/>
							}
							{step === 2 &&
								<Button text={t("next")}
									sizing='Medium' full maxWidth='154'
									disabled={!stepTwoStatus}
									submit={() => nextStep()}
								/>
							}
							{step === 3 &&
								<Button text={t("submit")}
									sizing='Medium' full maxWidth='154'
									disabled={!stepThreeBStatus}
									submit={() => checkRequiredFields()}
									id="simulate-provide-submit-button-id"
								/>
							}
						</div>
					</div>
				</div>
			</div>

			<Modal visible={isVisible} variant='medium' close={hideSimulateProvideCancelModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<IconConfigure />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Cancel Simulation
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(16) }}>Are you sure you want to cancel Simulation?</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {

									hideSimulateProvideCancelModal()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button text="Confirm"
								submit={() => {
									hideSimulateProvideCancelModal();
									// onCancel();
									goBack()
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={isSubmitModalVisible} variant='medium' close={hideSimulateProvideSubmitModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<IconConfigure />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Submit Simulation
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(16) }}>Are you sure you want to save Simulation?</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {
									hideSimulateProvideSubmitModal()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button text="Confirm"
								submit={() => {
									submit();
									hideSimulateProvideSubmitModal()
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			{
				submitLoading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src={assetUrl("/loading.gif")} />
							</div>
						</div>
						, document.body
					)
				)
			}

			<Toast2 show={error} errorMessage={error} close={()=>{setError()}} />
		</>
	)
}

export default SimulateProvide
